<template>
  <qtm-content-block v-bind="$attrs" title="Approval History">
    <approval-activity-item
      v-for="approval, i in formattedApprovals"
      :key="approval.id"
      :approval="approval"
      :final="i + 1 === formattedApprovals.length"
    />
  </qtm-content-block>
</template>

<script setup lang="ts">
import type { Order, PurchaseOrder } from '@quotetome/materials-api'
import ApprovalActivityItem from '@/components/orders/approval-activity-item.vue'

export interface Props {
  order: Order
  po?: PurchaseOrder
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  po: undefined,
  title: 'PO Activity',
})

const approvals = ref<any[]>([])
const loading = ref(true)

const filteredApprovals = computed(() => {
  if (!props.order.requiresAdjustments && props.po?.pending_revision) {
    const revision = props.po!.latest_change_order!.id

    return approvals.value.filter((approval) => approval.revision === revision)
  }

  return approvals.value
})

const formattedApprovals = computed(() => {
  if (!filteredApprovals.value.length) {
    return []
  }

  const app = filteredApprovals.value[0]
  const first = {
    ...filteredApprovals.value[0],
    id: 'first',
    title: 'Created',
    tag: 'by',
    actionUser: `${app.user.first_name} ${app.user.last_name}`,
  }

  return [first].concat(filteredApprovals.value
  .filter((entry, i) => entry.entry_type !== 'REQ' || filteredApprovals.value[i + 1]?.entry_type !== 'REQ')
  .map(approval => {
    let title = 'Reviewed'
    let tag = 'by'
    let actionUser = `${approval.user.first_name} ${approval.user.last_name}`
    let pending = false

    if (approval.entry_type === 'APR') {
      title = 'Approved'
    }
    else if (approval.entry_type === 'REQ') {
      title = 'Waiting for approval'
      tag = 'from'
      pending = true

      if (approval.requested_approver) {
        actionUser = `${approval.requested_approver.first_name} ${approval.requested_approver.last_name}`
      }
      else {
        actionUser = 'a team member'
      }
    }

    return {
      ...approval,
      actionUser,
      pending,
      title,
      tag,
    }
  }))
})

const { $api, $error } = useNuxtApp()

const fetchApprovals = async () => {
  try {
    approvals.value = await $api.v1.rfqs.approvals(props.order)
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

onMounted(fetchApprovals)
</script>
